package com.dotdrives.web.pages.dashboard

import com.dotdrives.web.ui.activateSurface
import com.dotdrives.controller.DashboardController
import com.dotdrives.controller.DashboardUiState
import com.dotdrives.model.DashboardGoal
import com.dotdrives.model.DashboardQueueItem
import com.dotdrives.repository.SupabaseDashboardRepository
import com.dotdrives.web.AppRoute
import com.dotdrives.web.entityRouteHash
import com.dotdrives.web.shell.NavItem
import com.dotdrives.web.shell.PortalShell
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotSkeleton
import com.dotdrives.web.ui.useDialogFocus
import com.dotdrives.web.ui.useFlowState
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import react.ChildrenBuilder
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.progress
import react.dom.html.ReactHTML.small
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.strong
import react.useEffect
import react.useState
import web.cssom.ClassName
import web.dom.ElementId
import web.html.ButtonType
import web.html.button as buttonType
import kotlin.math.roundToInt
import kotlin.time.Duration
import kotlin.time.Instant

external interface DashboardPortalProps : Props {
    var orgId: String
    var userId: String
    var userName: String
    var userInitials: String
    var orgName: String
    var canFinance: Boolean
    var canWrite: Boolean
    var onNavigate: (NavItem) -> Unit
    var onSwitchOrg: () -> Unit
    var onSignOut: () -> Unit
}

val DashboardPortal = FC<DashboardPortalProps> { props ->
    val (scope) = useState { MainScope() }
    val (controller) = useState {
        DashboardController(SupabaseDashboardRepository(), scope, props.orgId, props.canFinance)
    }
    val state = useFlowState(controller.state)
    useEffect(controller) {
        controller.load()
        try {
            awaitCancellation()
        } finally {
            scope.cancel()
        }
    }
    PortalShell {
        active = NavItem.Dashboard
        onNavigate = props.onNavigate
        userName = props.userName
        userInitials = props.userInitials
        orgName = props.orgName
        onSwitchOrg = props.onSwitchOrg
        onSignOut = props.onSignOut
        DashboardPage {
            this.state = state
            this.controller = controller
            name = props.userName
            canWrite = props.canWrite
            onNavigate = props.onNavigate
            onOpen = { item ->
                val route = if (item.subjectType == "drive") AppRoute.Drives else AppRoute.Givers
                window.location.hash = entityRouteHash(route, props.orgId, item.subjectId)
            }
        }
    }
}

external interface DashboardPageProps : Props {
    var state: DashboardUiState
    var controller: DashboardController
    var name: String
    var canWrite: Boolean
    var onNavigate: (NavItem) -> Unit
    var onOpen: (DashboardQueueItem) -> Unit
}

val DashboardPage = FC<DashboardPageProps> { props ->
    val state = props.state
    val snapshot = state.snapshot
    val finance = snapshot?.finance
    val (visible, setVisible) = useState(3)
    val (confirm, setConfirm) = useState<DashboardQueueItem?>(null)
    useDialogFocus(confirm != null, "dashboard-thank-dialog") { setConfirm(null) }
    val goal = finance?.goals?.firstOrNull { it.startsAt != null } ?: finance?.goals?.firstOrNull()

    DashboardStyles()
    div {
        className = ClassName("dashboard-page")
        div {
            className = ClassName("dashboard-intro")
            div {
                h1 {
                    +if (snapshot == null) "Your moves today"
                    else "Your ${state.queue.size} moves today, ${props.name.substringBefore(' ')}"
                }
                p {
                    +if (props.canWrite) {
                        "Ranked from overdue reminders, unthanked gifts, and drives in the same phase for over 30 days."
                    } else {
                        "Ranked from overdue reminders and drives in the same phase for over 30 days."
                    }
                }
            }
            if (props.canWrite) {
                button {
                    type = ButtonType.buttonType
                    className = ClassName("dashboard-primary")
                    onClick = { window.location.hash = com.dotdrives.web.workspaceHash("#/drives?new=1") }
                    +"+ New drive"
                }
            }
            if (finance != null) {
                div {
                    className = ClassName("dashboard-mobile-pace")
                    span { +"ORGANIZATION GOAL PACE" }
                    strong {
                        val forecast = goal?.forecast(snapshot.asOf)
                        +if (forecast != null) "${money(forecast)} / ${money(goal.amount)}"
                        else "Set a goal period to see pacing"
                    }
                }
            }
        }
        state.error?.let { DotBanner { message = it; onRetry = props.controller::load } }
        if (state.loading && snapshot == null) {
            div {
                className = ClassName("dashboard-loading")
                repeat(5) { DotSkeleton { height = "130px" } }
            }
        } else if (snapshot != null) {
            div {
                className = ClassName("dashboard-upper")
                div {
                    className = ClassName("dashboard-queue")
                    if (state.queue.isEmpty()) {
                        emptyQueue(props.canWrite) { props.onNavigate(NavItem.Givers) }
                    }
                    state.queue.take(visible).forEachIndexed { index, item ->
                        queueCard(
                            item = item,
                            index = index,
                            canWrite = props.canWrite,
                            pending = state.pendingId != null,
                            onComplete = {
                                if (item.kind == "gift") setConfirm(item) else props.controller.complete(item)
                            },
                            onOpen = { props.onOpen(item) },
                        )
                    }
                    div {
                        className = ClassName("dashboard-queue-foot")
                        if (visible < state.queue.size) {
                            button {
                                onClick = { setVisible(visible + 10) }
                                +"Show ${minOf(10, state.queue.size - visible)} more of ${state.queue.size - visible} remaining →"
                            }
                        } else {
                            +"All ${state.queue.size} ranked items shown. "
                            button {
                                onClick = { props.onNavigate(NavItem.NextSteps) }
                                +"See your next steps →"
                            }
                        }
                    }
                }
                if (finance != null) {
                    goalCard(goal, snapshot.asOf) { props.onNavigate(NavItem.Settings) }
                }
            }
            div {
                className = ClassName("dashboard-metrics")
                metric("GIVERS", snapshot.giverCount.toString(), "Organization directory")
                metric("ACTIVE DRIVES", snapshot.activeDrives.toString(), "Organization · open, unarchived")
                if (finance != null) {
                    metric("GIVING THIS YEAR", money(finance.yearTotal), "Organization · net gifts since Jan 1 UTC")
                    metric(
                        "GIVER RETENTION",
                        finance.retentionPercent?.let { "${it.roundToInt()}%" } ?: "—",
                        "${finance.retained} of ${finance.retentionBase} prior-year net-positive givers gave this year",
                    )
                } else {
                    metric("MY OVERDUE STEPS", snapshot.overdueCount.toString(), "Incomplete and past due")
                    metric("MY STALE DRIVES", snapshot.staleCount.toString(), "Over 30 days in the same phase")
                }
            }
            attentionCards(state.queue, finance != null) {
                setVisible(state.queue.size)
                window.scrollTo(0.0, 0.0)
            }
        }
    }
    confirm?.let { item ->
        div {
            className = ClassName("givers-backdrop dashboard-confirm")
            div {
                id = ElementId("dashboard-thank-dialog")
                className = ClassName("dashboard-confirm-card")
                role = "dialog".unsafeCast<AriaRole>()
                ariaModal = true
                ariaLabel = "Log thank-you"
                h2 { +"Log a completed thank-you" }
                p { +"Confirm that you personally thanked this giver. This records an acknowledgment; it does not send email." }
                button {
                    className = ClassName("dashboard-secondary")
                    onClick = { setConfirm(null) }
                    +"Cancel"
                }
                button {
                    className = ClassName("dashboard-primary")
                    onClick = { props.controller.complete(item); setConfirm(null) }
                    +"I thanked this giver"
                }
            }
        }
    }
}

private fun ChildrenBuilder.emptyQueue(canWrite: Boolean, onExplore: () -> Unit) {
    div {
        className = ClassName("dashboard-empty")
        span { +"✓" }
        h2 { +"You're clear for now" }
        p {
            +if (canWrite) "No overdue reminders, stale drives, or unacknowledged gifts assigned to you."
            else "No overdue reminders or stale drives assigned to you."
        }
        button { onClick = { onExplore() }; +"Explore your givers →" }
    }
}

private fun ChildrenBuilder.queueCard(
    item: DashboardQueueItem,
    index: Int,
    canWrite: Boolean,
    pending: Boolean,
    onComplete: () -> Unit,
    onOpen: () -> Unit,
) {
    div {
        key = "${item.kind}-${item.id}".unsafeCast<Key>()
        className = ClassName("dashboard-move dashboard-move-${item.kind}" + if (item.subjectType in listOf("giver", "drive")) " dot-surface" else "")
        onClick = { activateSurface(it) }
        div {
            className = ClassName("dashboard-move-title")
            span { className = ClassName("dashboard-rank"); +"${index + 1}" }
            h2 { +item.title }
            span {
                className = ClassName("dashboard-badge")
                +when (item.kind) {
                    "gift" -> "UNTHANKED"
                    "drive" -> "STALE DRIVE"
                    else -> "OVERDUE"
                }
            }
        }
        p {
            +when (item.kind) {
                "gift" -> "A personal thank-you has not been logged for this gift."
                "drive" -> "This drive has stayed in its current phase for more than 30 days."
                else -> "Your next step is overdue."
            }
            +" Since ${item.occurredAt.toString().take(10)}."
        }
        div {
            className = ClassName("dashboard-move-actions")
            if (canWrite && item.kind != "drive") {
                button {
                    type = ButtonType.buttonType
                    className = ClassName("dashboard-primary")
                    disabled = pending
                    onClick = { onComplete() }
                    +if (item.kind == "gift") "Log thank-you" else "✓ Mark done"
                }
            }
            if (item.subjectType in listOf("giver", "drive")) {
                button {
                    type = ButtonType.buttonType
                    className = ClassName("dashboard-secondary dot-surface-action")
                    onClick = { onOpen() }
                    +if (item.subjectType == "drive") "Open drive →" else "Open giver →"
                }
            }
        }
    }
}

private fun ChildrenBuilder.goalCard(goal: DashboardGoal?, asOf: Instant, onSetup: () -> Unit) {
    div {
        className = ClassName("dashboard-goal" + if (goal?.startsAt == null) " dot-surface" else "")
        onClick = { activateSurface(it) }
        span { className = ClassName("dashboard-meta"); +"ORGANIZATION · GOAL PACING" }
        h2 { +(goal?.name ?: "Set your goal") }
        if (goal == null) {
            p { +"Add a target and its goal period to track progress." }
            button { className = ClassName("dot-surface-action"); onClick = { onSetup() }; +"Set up a goal →" }
        } else {
            p {
                +if (goal.startsAt == null) "Undated target · all-time net gifts"
                else "${goal.startsAt.toString().take(10)} — ${goal.endsAt!!.minus(Duration.parse("1d")).toString().take(10)} · UTC"
            }
            div {
                className = ClassName("dashboard-goal-number")
                +money(goal.received)
                small { +" of ${money(goal.amount)}" }
            }
            progress {
                max = goal.amount
                value = goal.received.coerceIn(0.0, goal.amount)
                ariaLabel = "Goal progress"
            }
            goal.forecast(asOf)?.let { forecast ->
                div {
                    className = ClassName("dashboard-forecast")
                    span {
                        +if (goal.endsAt?.let { asOf >= it } == true) "FINAL PERIOD TOTAL" else "LINEAR PACE ESTIMATE"
                    }
                    strong { +money(forecast) }
                    p { +"Net gifts ÷ elapsed share of this goal's period. Assumes giving continues at the average rate so far." }
                }
            }
            if (goal.startsAt == null) {
                button { className = ClassName("dot-surface-action"); onClick = { onSetup() }; +"Add goal dates →" }
            }
            div {
                className = ClassName("dashboard-goal-details")
                div {
                    span { +"REMAINING" }
                    strong { +money((goal.amount - goal.received).coerceAtLeast(0.0)) }
                }
                div {
                    span { +"EXPECTED BY NOW" }
                    strong { +(goal.expected(asOf)?.let(::money) ?: "—") }
                }
            }
        }
        p {
            className = ClassName("dashboard-asof")
            +"As of ${asOf.toString().take(10)} UTC · reversals included"
        }
    }
}

private fun ChildrenBuilder.attentionCards(
    queue: List<DashboardQueueItem>,
    canFinance: Boolean,
    onViewQueue: () -> Unit,
) {
    div {
        className = ClassName("dashboard-attention")
        listOf("reminder" to "Overdue reminders", "drive" to "Stale drives", "gift" to "Unthanked gifts")
            .filter { canFinance || it.first != "gift" }
            .forEach { (kind, label) ->
                div {
                    className = ClassName("dashboard-attention-card dot-surface")
                    onClick = { activateSurface(it) }
                    h2 { +label; span { +queue.count { it.kind == kind }.toString() } }
                    val items = queue.filter { it.kind == kind }
                    if (items.isEmpty()) p { +"All clear" }
                    items.take(3).forEach { item ->
                        div {
                            className = ClassName("dashboard-attention-row")
                            span { +item.title }
                            small { +item.occurredAt.toString().take(10) }
                        }
                    }
                    button { className = ClassName("dot-surface-action"); onClick = { onViewQueue() }; +"View today's queue →" }
                }
            }
    }
}

private fun ChildrenBuilder.metric(label: String, value: String, detail: String) {
    div {
        className = ClassName("dashboard-metric")
        span { +label }
        strong { +value }
        p { +detail }
    }
}

fun money(amount: Double): String {
    val number = kotlin.math.abs(amount).asDynamic().toLocaleString(
        "en-US", js("({minimumFractionDigits:0,maximumFractionDigits:2})"),
    ) as String
    return "${if (amount < 0) "-" else ""}$$number"
}
